'use client';

/** @jsxImportSource @emotion/react */

import {
  Card,
  Chip,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  Separator,
  Typography,
  useBreakPoints,
  useTheme,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';

import { useThemeMode } from '@/context';
import { getCommonChipStyles } from '@/styles/Common.styles';

import { useStyles } from './WhyItWorks.styles';

const WhyItWorks = () => {
  const styles = useStyles();
  const t = useTranslations('WhyItWorks');
  const { theme } = useTheme();
  const { isDesktop } = useBreakPoints();
  const { isDark } = useThemeMode();

  const commonChip = getCommonChipStyles(theme);

  const cardData = [
    {
      id: 1,
      title: t('cards.singleSource.title'),
      description: t('cards.singleSource.description'),
      icon: 'single',
      color: '#3b82f6',
    },
    {
      id: 2,
      title: t('cards.governance.title'),
      description: t('cards.governance.description'),
      icon: 'governance',
      color: '#10b981',
    },
    {
      id: 3,
      title: t('cards.evolution.title'),
      description: t('cards.evolution.description'),
      icon: 'evolution',
      color: '#8b5cf6',
    },
    {
      id: 4,
      title: t('cards.infrastructure.title'),
      description: t('cards.infrastructure.description'),
      icon: 'infrastructure',
      color: '#f59e0b',
    },
  ];

  return (
    <section css={styles.wrapper} id="why-it-works">
      <Container css={styles.container}>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 0 }}
          padding={{ default: 0 }}
        >
          {/* Chip */}
          <FlexItem>
            <Chip
              label={t('chipLabel')}
              enableHoverEffect={false}
              customStyles={{
                chipContainer: commonChip.chipContainer,
                label: commonChip.chipLabel,
              }}
            />
          </FlexItem>

          {/* Header */}
          <FlexItem css={styles.headerSection}>
            <FlexLayout direction="column" alignItems="center" gap={16} padding={{ default: 0 }}>
              <Typography variant="h2" customStyles={styles.title}>
                {t('title')}
              </Typography>

              <Typography variant="p" customStyles={styles.description}>
                {t('description')}
              </Typography>
            </FlexLayout>
          </FlexItem>

          {/* Cards */}
          <FlexItem css={styles.cardsSection}>
            <div css={styles.cardGrid}>
              {cardData.map((item) => (
                <div key={item.id} css={styles.cardGridItem}>
                  <Card
                    appearance="basic"
                    effect="glow"
                    customStyles={{
                      container: css`
                        ${styles.cardContainer};

                        --card-glow-color: ${item.color};
                        --card-shadow-color: ${item.color};
                      `,
                      body: styles.cardBody,
                    }}
                  >
                    <FlexLayout
                      direction="column"
                      padding={{ default: 0 }}
                      css={styles.cardWrapper}
                    >
                      {/* Header */}
                      <FlexItem>
                        <FlexLayout
                          direction="row"
                          alignItems="center"
                          padding={{ default: 0 }}
                          css={styles.cardHeader}
                        >
                          <FlexItem>
                            <Icon
                              name={item.icon}
                              iconPath="/svgs"
                              color={item.color}
                              size={isDesktop ? 32 : 24}
                            />
                          </FlexItem>

                          <FlexItem>
                            <Typography variant="h3" customStyles={styles.cardTitle}>
                              {item.title}
                            </Typography>
                          </FlexItem>
                        </FlexLayout>
                      </FlexItem>

                      {/* Divider */}
                      <FlexItem>
                        <Separator
                          orientation="horizontal"
                          separatorColor={isDark ? '#424242' : '#BDBDBD'}
                        />
                      </FlexItem>

                      {/* Description */}
                      <FlexItem>
                        <Typography textAlign="start" css={styles.cardDescription}>
                          {item.description}
                        </Typography>
                      </FlexItem>
                    </FlexLayout>
                  </Card>
                </div>
              ))}
            </div>
          </FlexItem>
        </FlexLayout>
      </Container>
    </section>
  );
};

export default WhyItWorks;
